// FIELD VERIFY card (Howard, approved 2026-07-20) — the 3D panel's working
// machinery re-homed (re-mount, not rebuild) into the exact slot the 3D
// occupied: per-wall takeoff, TapeCheckPanel (identical writes: tape-check /
// score / freeze), taped appendage dims (the SAME DimEditRow rows, identical
// lp-appendage-dims writes), plus the "View Source Blueprints" entry —
// blueprint-path estimates only (live or CUT-archived run). Ships BEFORE the
// 3D flag flips so the tape workflow is never dark.
import React, { useEffect, useMemo, useState } from "react";
import { useT } from "@/lib/i18n";
import { Ruler, FileText, Camera, FileSpreadsheet, PencilRuler, Printer } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import TapeCheckPanel from "@/components/estimate/TapeCheckPanel";
import { buildHouseJson, DimEditRow } from "@/components/estimate/HouseModel3D";
import { crossCheckRidges } from "@/lib/gableMath";
import { PHOTO_ELEVATIONS_ENABLED } from "@/lib/featureFlags";

export default function FieldVerifyCard({ preview, estimate, runId, onDimsSaved, dimsRefreshKey }) {
  const t = useT();
  const [apDims, setApDims] = useState(() => estimate?.lp_appendage_dims || {});
  const [dimOffers, setDimOffers] = useState([]);
  const [bpRun, setBpRun] = useState(null);
  const [aiRun, setAiRun] = useState(null);

  useEffect(() => {
    if (!estimate?.id) return;
    api.get(`/measure/ai-measure/latest-for-estimate/${estimate.id}`)
      .then(({ data }) => setAiRun(data?.run || null))
      .catch(() => {});
  }, [estimate?.id]);

  useEffect(() => {
    if (!estimate?.id) return;
    api.get(`/estimates/${estimate.id}/lp-appendage-dims`)
      .then(({ data }) => { setApDims(data.dims || {}); setDimOffers(data.offers || []); })
      .catch(() => {});
  }, [estimate?.id, dimsRefreshKey]);

  useEffect(() => {
    if (!estimate?.id) return;
    api.get(`/measure/ai-blueprint/latest-for-estimate/${estimate.id}`)
      .then(({ data }) => setBpRun(data?.run || null))
      .catch(() => {});
  }, [estimate?.id]);

  // Identical write path to the retired 3D panel (server stays SSOT).
  const saveDim = async (key, field, value, source) => {
    try {
      const { data } = await api.post(`/estimates/${estimate.id}/lp-appendage-dims`,
        value == null ? { key, field, action: "revert" } : { key, field, value, source: source || "user" });
      setApDims((prev) => {
        const next = { ...prev, [key]: { ...(prev[key] || {}) } };
        if (value == null) delete next[key][field];
        else next[key][field] = { value: data.value, status: data.status, at: data.at, by: data.by };
        return next;
      });
      onDimsSaved?.();
    } catch {
      toast.error("Could not save the dimension — try again.");
    }
  };

  // DORMER DEPTH secondary entry (ruled 2026-07-26): writes back to the
  // IDENTICAL contractor_dormers field the annotator payload carries —
  // cheeks re-derive server-side, both surfaces stay in sync.
  const saveDormerDepth = async (idx, v) => {
    try {
      const { data } = await api.patch(
        `/measure/ai-measure/runs/${aiRun.run_id}/contractor-dormers/${idx}`,
        { depth_ft: v === "" ? null : v });
      setAiRun((prev) => {
        const rows = [...(prev.contractor_dormers || [])];
        rows[idx] = { ...rows[idx], depth_ft: data.depth_ft, cheeks_ft: data.cheeks_ft };
        return { ...prev, contractor_dormers: rows };
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save dormer depth — try again.");
    }
  };

  const house = useMemo(() => {
    try {
      return buildHouseJson(preview, { pitch: null, eaveHeights: {}, widths: {} }, estimate, apDims);
    } catch {
      return null;
    }
  }, [preview, estimate, apDims]);

  const peb = preview?.measurements?._per_elevation_breakdown || [];
  const appendages = house?.appendages || [];

  // Adaptive source-view door (ruled 2026-07-20): ONE link riding this
  // card on ALL estimates; label adapts to the bound intake door with
  // precedence blueprint → photo → hover. No substrate on any door →
  // no link at all (named empty state on the page, never a dead link).
  const isHoverRun = aiRun && (aiRun.source === "hover" || String(aiRun.run_id || "").startsWith("hover-"));
  const door = bpRun ? "blueprint"
    : (aiRun && !isHoverRun && (aiRun.photo_paths || aiRun.result?.raw_ai) ? "photo"
      : (isHoverRun ? "hover" : null));
  const DOOR_LABEL = {
    blueprint: t("fv.door.blueprint"),
    photo: t("fv.door.photo"),
    hover: t("fv.door.hover"),
  };
  const DoorIcon = door === "photo" ? Camera : door === "hover" ? FileSpreadsheet : FileText;

  // Elevation-sheet chooser (ruled 2026-07-20): photo estimates get the
  // EL-1..EL-4 links beside the source-view door, same prominence — one
  // link per wall the bound run actually carries (never a dead link);
  // photo estimates whose substrate can't render sheets get a NAMED
  // empty-state chip instead.
  const SHEET_CODES = { front: "EL-1", left: "EL-2", back: "EL-3", right: "EL-4" };
  const sheetWalls = door === "photo" && aiRun?.status === "done"
    ? ["front", "left", "back", "right"].filter((w) =>
        ((aiRun.result?.raw_ai?.walls) || []).some((x) => String(x.label || "").toLowerCase() === w))
    : [];

  return (
    <div className="space-y-3" data-testid="field-verify-card">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-[11px] font-bold uppercase tracking-wider text-[var(--ink-2)] flex items-center gap-2">
          <Ruler className="w-3 h-3" /> {t("fv.title")}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {door && (
            <a
              href={`/estimate/${estimate.id}/source-view`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[var(--ai)] text-[var(--ai)] text-[10px] font-bold uppercase tracking-wider hover:bg-[var(--ai)] hover:text-white transition-colors"
              data-testid="field-verify-source-link"
              title="Read-only source view — the exact intake evidence the AI worked from, with per-item provenance (opens in a new tab — the modal stays put)"
            >
              <DoorIcon className="w-3 h-3" /> {DOOR_LABEL[door]}
            </a>
          )}
          {PHOTO_ELEVATIONS_ENABLED && door === "photo" && (sheetWalls.length > 0 ? (
            <span className="inline-flex items-center gap-1.5" data-testid="field-verify-elevation-sheets">
              <PencilRuler className="w-3 h-3 text-[var(--ai)]" />
              {sheetWalls.map((w) => (
                <a
                  key={w}
                  href={`/estimate/${estimate.id}/elevation-sheet/${w}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-2 py-1.5 border border-[var(--ai)] text-[var(--ai)] text-[10px] font-bold uppercase tracking-wider hover:bg-[var(--ai)] hover:text-white transition-colors"
                  data-testid={`field-verify-elevation-sheet-link-${w}`}
                  title={`${SHEET_CODES[w]} — ${w} elevation sheet, drawn from the run this takeoff binds (live or CUT-archived). Opens in a new tab — the modal stays put.`}
                >
                  {SHEET_CODES[w]} {t(`fv.wall.${w}`)}
                </a>
              ))}
              <a
                href={`/estimate/${estimate.id}/elevation-sheets/print`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 px-2 py-1.5 border border-[var(--ai)] bg-[var(--ai)] text-white text-[10px] font-bold uppercase tracking-wider hover:opacity-90 transition-opacity"
                data-testid="field-verify-elevation-sheets-print-all"
                title="One print flow — every renderable elevation sheet, one per page (leave-behind package). Opens in a new tab — the modal stays put."
              >
                <Printer className="w-3 h-3" /> {t("fv.printAll")}
              </a>
            </span>
          ) : (
            <span
              className="inline-flex items-center px-3 py-1.5 border border-dashed border-[var(--border)] text-[var(--muted)] text-[10px] font-bold uppercase tracking-wider"
              data-testid="field-verify-elevation-sheets-empty"
              title="Sheets bind to a completed AI measure run carrying walls — none exists for this estimate yet"
            >
              {t("fv.sheetsEmpty")}
            </span>
          ))}
        </div>
      </div>

      {peb.length > 0 && (
        <div className="p-3 bg-[var(--surface)] border border-[var(--border)]" data-testid="field-verify-per-wall">
          <div className="text-[10px] uppercase tracking-wider text-[var(--muted)] font-bold mb-1.5">
            {t("fv.perWall")} <span className="text-[9px] italic font-normal">{t("fv.perWall.sub")}</span>
          </div>
          <table className="w-full text-[11px]">
            <thead>
              <tr className="text-[9px] uppercase tracking-wider text-[var(--muted)]">
                <th className="text-left py-0.5">{t("fv.th.wall")}</th>
                <th className="text-right py-0.5">{t("fv.th.body")}</th>
                <th className="text-right py-0.5">{t("fv.th.gable")}</th>
                <th className="text-right py-0.5">{t("fv.th.dormer")}</th>
                <th className="text-right py-0.5">{t("fv.th.total")}</th>
              </tr>
            </thead>
            <tbody>
              {peb.map((r, i) => {
                const total = (r.wall_body_sqft || 0) + (r.gable_sqft || 0) + (r.dormer_sqft || 0);
                return (
                  <tr key={i} className="border-t border-[var(--border)]" data-testid={`field-verify-wall-row-${i}`}>
                    <td className="py-1 uppercase font-bold text-[var(--ink-2)]">{t(`fv.wall.${String(r.label || "").toLowerCase()}`) || r.label}</td>
                    <td className="py-1 text-right font-mono-num">{Math.round(r.wall_body_sqft || 0)}</td>
                    <td className="py-1 text-right font-mono-num">{Math.round(r.gable_sqft || 0)}</td>
                    <td className="py-1 text-right font-mono-num">{Math.round(r.dormer_sqft || 0)}</td>
                    <td className="py-1 text-right font-mono-num font-bold">{Math.round(total)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Contractor gables (TAPED-class, ruled 2026-07-24) — separate,
          visible rows; assignable their own waste factor later. Never
          injected into derivation. */}
      {(aiRun?.contractor_gables || []).length > 0 && (() => {
        const gbs = aiRun.contractor_gables;
        const rises = {};
        for (const g of gbs) {
          if (g.rise_ft != null) (rises[g.elevation] = rises[g.elevation] || []).push(g.rise_ft);
        }
        const ridgeWarn = crossCheckRidges(rises);
        return (
          <div className="p-3 bg-[var(--surface)] border border-[#16A34A]/40 space-y-1" data-testid="field-verify-contractor-gables">
            <div className="text-[10px] uppercase tracking-wider text-[#15803D] font-bold mb-1">
              {t("fv.gables.title")} <span className="text-[9px] italic font-normal">{t("fv.gables.sub")}</span>
            </div>
            {gbs.map((g, i) => (
              <div key={i} className="flex items-center justify-between text-[11px] py-0.5 border-t border-[var(--border)] first:border-t-0" data-testid={`contractor-gable-row-${i}`}>
                <span className="uppercase tracking-wider text-[var(--ink-2)] font-bold">{g.elevation}</span>
                <span className="font-mono-num">
                  {g.base_ft != null && g.rise_ft != null
                    ? `${g.base_ft} ft × ${g.rise_ft} ft = ${g.area_ft} ft²${g.pitch != null ? ` · ${g.pitch}/12` : ""}${g.masked_ft > 0 ? ` (−${g.masked_ft} ft² masks)` : ""}`
                    : t("fv.noScale")}
                </span>
              </div>
            ))}
            {ridgeWarn && (
              <div className="text-[10px] font-bold text-[#B45309] pt-1" data-testid="contractor-gable-ridge-warning">
                ⚠ {ridgeWarn}
              </div>
            )}
          </div>
        );
      })()}

      {/* Contractor dormers (TAPED-class, ruled 2026-07-25) — separate,
          visible rows; assignable their own waste factor later. Never
          injected into derivation. */}
      {(aiRun?.contractor_dormers || []).length > 0 && (
        <div className="p-3 bg-[var(--surface)] border border-[#16A34A]/40 space-y-1" data-testid="field-verify-contractor-dormers">
          <div className="text-[10px] uppercase tracking-wider text-[#15803D] font-bold mb-1">
            {t("fv.dormers.title")} <span className="text-[9px] italic font-normal">{t("fv.gables.sub")}</span>
          </div>
          {aiRun.contractor_dormers.map((d, i) => (
            <div key={i} className="flex items-center justify-between gap-2 text-[11px] py-0.5 border-t border-[var(--border)] first:border-t-0" data-testid={`contractor-dormer-row-${i}`}>
              <span className="uppercase tracking-wider text-[var(--ink-2)] font-bold">{d.elevation}</span>
              <span className="font-mono-num flex items-center gap-2 flex-wrap justify-end">
                {d.width_ft != null && d.height_ft != null ? (
                  <span>
                    {t("fv.frontFace")} {d.width_ft} ft × {d.height_ft} ft = {d.area_ft} ft²{d.masked_ft > 0 ? ` (−${d.masked_ft} ft² masks)` : ""}
                    {(() => {
                      // blank depth → 1.5 ft smart default (ruled 2026-07-26
                      // follow-up) — cheeks always calculate when height known
                      const eff = d.depth_ft || 1.5;
                      const ch = d.cheeks_ft || Math.round(2 * d.height_ft * eff * 10) / 10;
                      return (
                        <span data-testid={`contractor-dormer-cheeks-${i}`}>
                          {" "}{t("fv.cheeks")} 2 × {d.height_ft}×{eff} = {ch} ft²{d.depth_ft ? "" : ` ${t("fv.defaultDepth")}`} ({t("fv.total")} {Math.round(((d.area_ft || 0) + ch) * 10) / 10} ft²)
                        </span>
                      );
                    })()}
                  </span>
                ) : t("fv.noScale")}
                <label className="text-[10px] text-[var(--muted)]">{t("fv.depthFt")}</label>
                <input
                  key={`${i}-${d.depth_ft ?? ""}`}
                  type="number" min="0" step="0.1" inputMode="decimal"
                  defaultValue={d.depth_ft ?? 1.5}
                  onBlur={(e) => { if (e.target.value !== String(d.depth_ft ?? "")) saveDormerDepth(i, e.target.value); }}
                  className="w-14 text-[11px] border border-[var(--border)] bg-[var(--surface)] px-1 py-0.5 font-mono-num"
                  data-testid={`contractor-dormer-depth-input-${i}`}
                />
              </span>
            </div>
          ))}
        </div>
      )}

      {appendages.length > 0 && (
        <div className="p-3 bg-[var(--surface)] border border-[var(--border)] space-y-1" data-testid="field-verify-taped-dims">
          <div className="text-[10px] uppercase tracking-wider text-[var(--muted)] font-bold mb-1">
            {t("fv.tapedDims.title")} <span className="text-[9px] italic font-normal">{t("fv.tapedDims.sub")}</span>
          </div>
          {appendages.map((ap, i) => {
            const apKey = `appendage:${ap.originalWall || ap.wall}`;
            return (
              <div key={i} className="py-1 border-t border-[var(--border)] first:border-t-0">
                <div className="text-[10px] font-bold uppercase tracking-wider text-[var(--ink-2)]">
                  {t("fv.wallRow", { kind: ap.kind || t("fv.appendage"), wall: t(`fv.wall.${String(ap.wall || "").toLowerCase()}`) || ap.wall })}
                </div>
                <DimEditRow
                  label={t("fv.depth")} field="depth_ft" apKey={apKey}
                  entry={(apDims[apKey] || {}).depth_ft}
                  valueFt={ap.depthFt}
                  fallbackText={ap.depthSource === "printed"
                    ? t("fv.printedOnPlans", { v: ap.depthFt.toFixed(1) })
                    : t("fv.assumedDepth", { v: ap.depthFt.toFixed(0) })}
                  offer={(dimOffers.find((o) => o.key === apKey) || {}).depth_ft}
                  onSave={saveDim}
                  testid={`field-verify-appendage-depth-${i}`}
                />
                <DimEditRow
                  label={t("fv.height")} field="height_ft" apKey={apKey}
                  entry={(apDims[apKey] || {}).height_ft}
                  valueFt={ap.heightFt}
                  fallbackText={ap.heightSource === "printed"
                    ? t("fv.printedOnPlans", { v: ap.heightFt.toFixed(1) })
                    : t("fv.assumedHeight")}
                  offer={(dimOffers.find((o) => o.key === apKey) || {}).height_ft}
                  onSave={saveDim}
                  testid={`field-verify-appendage-height-${i}`}
                />
              </div>
            );
          })}
        </div>
      )}

      <TapeCheckPanel
        estimateId={estimate?.id}
        runId={runId}
        facades={house?.facades || []}
        dormers={house?.roof?.dormers || []}
      />
    </div>
  );
}
